export interface FileMetadata {
  name: string
  mime: string
  size?: number
}

const mimeByExtension: Record<string, string> = {
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  rtf: 'application/msword',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  apng: 'image/apng',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  jfif: 'image/jpeg',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
}

const knownFiles = new Map<string, FileMetadata>()
const requests = new Map<string, Promise<FileMetadata>>()

const previewableImageMimes = new Set([
  'image/jpeg',
  'image/png',
  'image/apng',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/svg+xml',
  'image/x-icon',
  'image/vnd.microsoft.icon',
])

/** Native browser previews; formats such as HEIC/TIFF remain downloadable file tiles. */
export const isPreviewableImageFile = (metadata: Pick<FileMetadata, 'mime'>) =>
  previewableImageMimes.has(metadata.mime.toLowerCase().split(';')[0].trim())

export const fileExtension = (name: string) => /\.([^.]+)$/.exec(name)?.[1].toLowerCase() || ''

export const safeFileUrl = (value: string) => {
  try {
    const url = new URL(value, window.location.href)
    return ['http:', 'https:', 'blob:'].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}

const resolveMime = (name: string, mime?: string) =>
  mime && mime !== 'application/octet-stream' ? mime : mimeByExtension[fileExtension(name)] || ''

export function getFileMetadata(url: string): FileMetadata {
  const known = knownFiles.get(url)
  if (known) return known
  let name = 'File'
  try {
    const segment = new URL(url, window.location.href).pathname.split('/').pop()
    if (segment) {
      try {
        name = decodeURIComponent(segment)
      } catch {
        name = segment
      }
      // Upload URLs prefix the original filename with the uploader and a generated hash.
      name = name.replace(/^.+?_[a-f0-9]{16,64}[-_]/i, '')
    }
  } catch {
    /* Keep a readable fallback for malformed legacy URLs. */
  }
  return { name, mime: resolveMime(name) }
}

/** Keep exact metadata for newly uploaded documents without changing the comments API's URL array. */
export function rememberFileMetadata(url: string, file: Pick<File, 'name' | 'type' | 'size'>) {
  knownFiles.set(url, { name: file.name, mime: resolveMime(file.name, file.type), size: file.size })
}

export function loadFileMetadata(url: string): Promise<FileMetadata> {
  const known = getFileMetadata(url)
  if (known.size !== undefined) return Promise.resolve(known)
  const href = safeFileUrl(url)
  if (!href || !/^https?:/.test(href)) return Promise.resolve(known)
  const pending = requests.get(url)
  if (pending) return pending
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 8000)
  const request = fetch(href, {
    method: 'HEAD',
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    signal: controller.signal,
  })
    .then((response) => {
      if (!response.ok) return known
      const length = response.headers.get('content-length')
      const bytes = length?.trim() ? Number(length) : NaN
      const metadata = {
        ...known,
        mime: resolveMime(known.name, response.headers.get('content-type')?.split(';')[0].trim()),
        ...(Number.isFinite(bytes) && bytes >= 0 ? { size: bytes } : {}),
        ...knownFiles.get(url),
      }
      knownFiles.set(url, metadata)
      return metadata
    })
    .catch(() => getFileMetadata(url))
    .finally(() => window.clearTimeout(timeout))
  // Reuse even a failed lookup: blocked CORS must not trigger requests on every comment render.
  requests.set(url, request)
  return request
}
